import type { FlowchartSubgraph } from './flowchart-parser-model';
import type { ResolvedFlowchart } from './resolve-flowchart';
import type { NodeBox } from './flowchart-layout-types';

interface TopLevelEntity {
  id: string;
  descendantIds: string[];
}

interface ComponentBlock {
  entityIds: string[];
  boxIds: string[];
  minX: number;
  minY: number;
  width: number;
  height: number;
}

interface ComponentOffset {
  dx: number;
  dy: number;
}

const COMPONENT_GAP_X = 96;
const COMPONENT_GAP_Y = 72;
const COMPONENT_ROW_TARGET_WIDTH = 1280;

function getTopLevelAncestorId(input: {
  id: string;
  parentById: ReadonlyMap<string, string | null>;
}): string {
  let currentId = input.id;
  let parentId = input.parentById.get(currentId);
  while (parentId) {
    currentId = parentId;
    parentId = input.parentById.get(currentId);
  }
  return currentId;
}

function collectTopLevelEntities(input: {
  resolved: ResolvedFlowchart;
  parentById: ReadonlyMap<string, string | null>;
}): TopLevelEntity[] {
  const descendantsByTop = new Map<string, string[]>();
  const ensure = (topId: string): string[] => {
    const existing = descendantsByTop.get(topId);
    if (existing) {
      return existing;
    }
    const created: string[] = [];
    descendantsByTop.set(topId, created);
    return created;
  };

  input.resolved.subgraphs.forEach((subgraph) => {
    const topId = getTopLevelAncestorId({ id: subgraph.id, parentById: input.parentById });
    ensure(topId).push(subgraph.id);
  });
  input.resolved.nodes.forEach((node) => {
    const topId = getTopLevelAncestorId({ id: node.id, parentById: input.parentById });
    ensure(topId).push(node.id);
  });

  return Array.from(descendantsByTop.entries()).map(([id, descendantIds]) => ({
    id,
    descendantIds: descendantIds.includes(id) ? descendantIds : [id, ...descendantIds],
  }));
}

function buildComponentGroups(input: {
  entities: readonly TopLevelEntity[];
  resolved: ResolvedFlowchart;
  parentById: ReadonlyMap<string, string | null>;
}): TopLevelEntity[][] {
  const parent = new Map<string, string>();
  input.entities.forEach((entity) => parent.set(entity.id, entity.id));

  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) !== root) {
      root = parent.get(root) ?? root;
    }
    let cursor = id;
    while (parent.get(cursor) !== root) {
      const next = parent.get(cursor) ?? root;
      parent.set(cursor, root);
      cursor = next;
    }
    return root;
  };

  const union = (left: string, right: string): void => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) {
      parent.set(leftRoot, rightRoot);
    }
  };

  input.resolved.edges.forEach((edge) => {
    const fromTop = getTopLevelAncestorId({ id: edge.from, parentById: input.parentById });
    const toTop = getTopLevelAncestorId({ id: edge.to, parentById: input.parentById });
    if (parent.has(fromTop) && parent.has(toTop)) {
      union(fromTop, toTop);
    }
  });

  const groupsByRoot = new Map<string, TopLevelEntity[]>();
  input.entities.forEach((entity) => {
    const root = find(entity.id);
    const group = groupsByRoot.get(root) ?? [];
    group.push(entity);
    groupsByRoot.set(root, group);
  });

  return Array.from(groupsByRoot.values());
}

function measureComponentBlock(input: {
  group: readonly TopLevelEntity[];
  nodeBoxesById: ReadonlyMap<string, NodeBox>;
}): ComponentBlock {
  const boxIds = input.group.flatMap((entity) => entity.descendantIds);
  const boxes = boxIds
    .map((boxId) => input.nodeBoxesById.get(boxId))
    .filter((box): box is NodeBox => box != null);
  const minX = boxes.reduce((value, box) => Math.min(value, box.x), Number.POSITIVE_INFINITY);
  const minY = boxes.reduce((value, box) => Math.min(value, box.y), Number.POSITIVE_INFINITY);
  const maxX = boxes.reduce((value, box) => Math.max(value, box.x + box.width), Number.NEGATIVE_INFINITY);
  const maxY = boxes.reduce((value, box) => Math.max(value, box.y + box.height), Number.NEGATIVE_INFINITY);
  const hasBoxes = boxes.length > 0;
  return {
    entityIds: input.group.map((entity) => entity.id),
    boxIds,
    minX: hasBoxes ? minX : 0,
    minY: hasBoxes ? minY : 0,
    width: hasBoxes ? maxX - minX : 0,
    height: hasBoxes ? maxY - minY : 0,
  };
}

function packComponentBlocks(blocks: readonly ComponentBlock[]): Map<string, ComponentOffset> {
  const offsets = new Map<string, ComponentOffset>();
  if (blocks.length <= 1) {
    return offsets;
  }

  const ordered = [...blocks].sort((left, right) => right.height - left.height);
  let cursorX = 0;
  let cursorY = 0;
  let rowHeight = 0;

  ordered.forEach((block) => {
    const exceedsRow = cursorX > 0 && cursorX + block.width > COMPONENT_ROW_TARGET_WIDTH;
    if (exceedsRow) {
      cursorX = 0;
      cursorY += rowHeight + COMPONENT_GAP_Y;
      rowHeight = 0;
    }

    const dx = cursorX - block.minX;
    const dy = cursorY - block.minY;
    block.boxIds.forEach((boxId) => offsets.set(boxId, { dx, dy }));

    cursorX += block.width + COMPONENT_GAP_X;
    rowHeight = Math.max(rowHeight, block.height);
  });

  return offsets;
}

function buildParentMap(input: { resolved: ResolvedFlowchart }): Map<string, string | null> {
  const parentById = new Map<string, string | null>();
  input.resolved.subgraphs.forEach((subgraph: FlowchartSubgraph) =>
    parentById.set(subgraph.id, subgraph.parent),
  );
  input.resolved.nodes.forEach((node) => parentById.set(node.id, node.parent));
  return parentById;
}

export function packDisconnectedComponents(input: {
  resolved: ResolvedFlowchart;
  nodeBoxesById: Map<string, NodeBox>;
}): void {
  const parentById = buildParentMap({ resolved: input.resolved });
  const entities = collectTopLevelEntities({ resolved: input.resolved, parentById });
  const groups = buildComponentGroups({ entities, resolved: input.resolved, parentById });
  if (groups.length <= 1) {
    return;
  }

  const blocks = groups.map((group) => measureComponentBlock({ group, nodeBoxesById: input.nodeBoxesById }));
  const offsets = packComponentBlocks(blocks);
  offsets.forEach((offset, boxId) => {
    const box = input.nodeBoxesById.get(boxId);
    if (box) {
      input.nodeBoxesById.set(boxId, { ...box, x: box.x + offset.dx, y: box.y + offset.dy });
    }
  });
}
